import {
  AfterContentInit, Component, ElementRef, EventEmitter, Input, NgModule, OnChanges,
  OnInit, Output, Renderer2, SimpleChanges, ViewChild, ViewEncapsulation,
} from '@angular/core';
import { DatePicker, DatePickerModule } from 'primeng/datepicker';
import { FFacetInfoModule } from '@wipo/w-angular/internal';

import { PrimeLocale, WInputBase } from '@wipo/w-angular/shared';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import {
  CreateControlAccessProvider, propChanged,
} from '@wipo/w-angular/shared';
import { fromEvent } from 'rxjs';
import { WHideEmptyModule } from '@wipo/w-angular/directives';
import { WButtonModule } from '@wipo/w-angular/w-button';
import { PrimeNG } from 'primeng/config';

@Component({
  selector: 'w-input-date',
  providers: [CreateControlAccessProvider(WInputDateComponent)],
  templateUrl: './w-input-date.component.html',
  host: {
    class: 'b-input b-input-dropdown b-input-date ',
    '[class.b-input--has-label]': '!!label',
    '[class.b-input--has-floating-label]': '!!label && !!value',
  },
  standalone: false
})
/*
@id w-calendar
@name w-input-date
@status release
@description
@dependencies [primeng]
*/
export class WInputDateComponent extends WInputBase implements OnChanges, OnInit {
  @ViewChild('datepicker', { static: true }) picker!: DatePicker;

  // tslint:disable-next-line: max-line-length
  @Input() dateFormat: string = ''; // date pattern (overrides default pattern from language related locale)
  @Input() yearRange: string; // 'yearFrom:yearTo', sets min and max date if provided
  @Input() pagedate: Date = new Date(); // current calendar page
  @Input() maxdate: Date; //  latest selectable date, can be implied providing yearRange
  @Input() mindate: Date; //  earliest selectable date, can be implied providing yearRange
  @Input() readonlyInput: boolean = false; // cannot select date
  @Input() showTime: boolean = false; // show time selection
  @Input() showButtonBar: boolean = true; // show button bar with today and clear buttons
  @Input() language?: string; // change of language in one component will apply for all inputs using primeng i18n
  @Input() selectionMode: "single" | "multiple" | "range" = "single"; // single, multiple, range

  private open = false; // keep current state
  firstDayOfWeek: number = 0;
  localeDateFormat?: string;
  private now = new Date();

  get effectivePlaceholder(): string {
    return this.placeholder ? this.placeholder : this.dateFormat;
  }

  get _dateFormat() {
    return this.dateFormat ? this.dateFormat : this.localeDateFormat;
  }
  @Output() wDateSelect = new EventEmitter();
  
  debug: boolean = false;

  // tslint:disable-next-line: variable-name
  constructor(protected _renderer: Renderer2,
    protected _elementRef: ElementRef, private config: PrimeNG) {
    super();
    this.yearRange = `${this.dateAdjustYear(-60)}:${this.dateAdjustYear(60)}`;
    const years = this.yearRange.split(':');
    this.mindate = new Date(+years[0], 0, 1);
    this.maxdate = new Date(+years[1], 11, 31);
  }
  override ngOnInit(): void {
    super.ngOnInit();
    // run this only if language has been set
    this.initLocalization();
    PrimeLocale.locale.subscribe(this.updateLocale.bind(this));
    this.picker.onShow.subscribe((state: any) => {
      this.open = true;
    });
    this.picker.onClose.subscribe((state: any) => {
      setTimeout(() => {
        // if triggered by user clicking on calendar icon, leave time the button to process click event (NOOP)
        this.open = false;
      }, 500);
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (this.language && propChanged(changes, 'language')) {
      this.initLocalization(this.language);
    }
    // min/max date and year range
    try {
      const yr = changes['yearRange'];
      const miD = changes['mindate'];
      const mxD = changes['maxdate'];
      if (this.yearRange && yr?.currentValue !== yr?.previousValue) {
        const years = yr?.currentValue.split(':');
        this.mindate = new Date(years[0], 0, 1);
        this.maxdate = new Date(years[1], 11, 31);
      } else if (this.mindate && miD?.currentValue !== miD?.previousValue) {
        // set year from min date, else now
        this.yearRange = `${this.mindate.getFullYear()}:${this.getYear(this.maxdate)}`;
      } else if (this.maxdate && mxD?.currentValue !== mxD?.previousValue) {
        // set year from min date, else now
        this.yearRange = `${this.getYear(this.mindate)}:${this.maxdate?.getFullYear()}`;
      }
    } catch (e) {
      console.error(e);
    }
  }
  private getYear(d: Date) {
    return d?.getFullYear() || this.now.getFullYear();
  }
  private dateAdjustYear(y: number) {
    return this.now.getFullYear() + y;
  }
  private updateLocale(locale?: Record<string, any>) {
    // tslint:disable-next-line: no-string-literal
    this.localeDateFormat = locale ? (locale['dateFormat'] as string || 'dd/mm/y') : 'dd/mm/y';

    // tslint:disable-next-line: no-string-literal
    this.firstDayOfWeek = locale ? locale['firstDayOfWeek'] : 0;
  }
  private initLocalization(language?: string) {
    // have version incompatibility. try to land it
    if ((this.config as any).getTranslation) {
      if (language) {
        // if language changed, init
        PrimeLocale.initFrom(this.config, language);
      }
      // else assume someone else have initialized (without specific language in component)
      this.updateLocale();
    } else {
      console.error('INVALID PrimeNg version, configuration:', this.config);
      this.localeDateFormat = 'dd/mm/y';
      this.firstDayOfWeek = 0;
    }
  }
  toggleCalendar(event: MouseEvent) {
    if (!this.open) {
      // here only open. close handled by primeng on click-outside of calendar component
      const input = this.picker.el.nativeElement.querySelector('input[pinputtext]');
      input.focus();
    }
  }

  onSelect(event: any) {
    this.wDateSelect.emit(event);
  }
  onBlur(e: Event) {
    this.wBlur.next(e as FocusEvent);
  }
  onFocus(e: Event) {
    this.wFocus.next(e as FocusEvent);
  }

}
@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    DatePickerModule,
    WHideEmptyModule, WButtonModule,
    FFacetInfoModule
  ],
  exports: [WInputDateComponent],
  declarations: [WInputDateComponent]
})
export class WInputDateModule { }
